import React, { useEffect } from 'react';

import MiniOverlayModal from 'dpl/common/mini_overlay_modal/components/MiniOverlayModal';
import FixedTooltipPortal from 'dpl/common/tooltip/components/FixedTooltipPortal';
import { isMobileUA } from 'dpl/shared/utils';

const IS_MOBILE_UA = isMobileUA();

interface CheckoutSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  surfaceClassName?: string;
  dataTestId?: string;
}

// A bottom sheet on phones, a centered modal elsewhere. On phones the portal
// stays mounted and only toggles visibility, so dropdowns that portal from
// inside the sheet land after it in <body> and open on top of it; children
// still unmount while it's closed, so anything stateful inside resets on
// each open.
export default function CheckoutSheet({
  isOpen,
  onClose,
  title = null,
  children,
  className = '',
  surfaceClassName = 'bg-white',
  dataTestId = 'checkout-sheet'
}: CheckoutSheetProps) {
  // Every phone sheet, dropdown and tooltip shares the `no-scroll` class on
  // body, and the ones inside this sheet take it off when they mount hidden
  // or close. Put it back while this sheet is open so the page under it stays
  // locked. Disconnecting on close drops anything not yet delivered, so the
  // sheet's own close still unlocks the page.
  useEffect(() => {
    if (!IS_MOBILE_UA || !isOpen) return undefined;

    const { body } = window.document;
    const observer = new MutationObserver(() => {
      if (!body.classList.contains('no-scroll')) {
        body.classList.add('no-scroll');
      }
    });
    observer.observe(body, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, [isOpen]);

  const content = (
    <div className={className} data-test-id={dataTestId}>
      {title && (
        <h4 className="font-serif font-24 lh-title fw-normal mb5 mr10">
          {title}
        </h4>
      )}
      {children}
    </div>
  );

  if (IS_MOBILE_UA) {
    return (
      <FixedTooltipPortal
        isVisible={isOpen}
        onCloseClick={onClose}
        style={{ maxHeight: '90vh' }}
        className={`pt8 ${surfaceClassName}`}
      >
        <div
          className="absolute top-0 left-0 right-0 flex justify-center pt2"
          aria-hidden
        >
          <div
            className="bg-gray-300 br-pill"
            style={{ width: 36, height: 4 }}
          />
        </div>
        {content}
      </FixedTooltipPortal>
    );
  }

  if (!isOpen) return null;

  return (
    <MiniOverlayModal
      maxWidth="480px"
      onCloseClick={onClose}
      closeButtonClassName="pv2 ph2 mt4 mr4 ba b--light-gray bg-white br-100"
      contentClassName={`${surfaceClassName} pv8 ph6`}
    >
      {content}
    </MiniOverlayModal>
  );
}
